import { apply, nodeMatrix, type PhotoDimensions } from '@repo/design-renderer';
import type { PointerEvent, ReactElement } from 'react';
import {
  type BoxNode,
  type HandleKind,
  handleCursor,
  type WarpedSlot,
  warpedHandles,
  warpedRingPieces,
} from '@/features/design-editor';
import { RotateHandle } from './rotate-handle';

/** 화면 위 사진 상자(CSS px) — 사진 px 에 width / photo.widthPx 를 곱해 옮긴다. */
export interface PhotoBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

interface WarpedSelectionOverlayProps {
  readonly slot: WarpedSlot;
  readonly node: BoxNode;
  readonly photo: PhotoDimensions;
  readonly box: PhotoBox;
  readonly onPointerDownHandle: (kind: HandleKind, event: PointerEvent<SVGElement>) => void;
  /** 회전 손잡이를 끄는 중인가 — 커서를 grabbing 으로 둔다. */
  readonly rotating?: boolean;
}

/** 테두리를 나누는 간격(mm) — 큰 노드도 휜 모양이 매끄럽게 보이도록 변 길이의 1/48, 최소 0.5mm. */
const ringStepOf = (node: BoxNode): number => Math.max(0.5, Math.max(node.width, node.height) / 48);

/**
 * 휜 표면 위 선택 테두리와 핸들. 테두리는 노드 상자(회전 반영)를 잘게 나눠 사진 px 로 옮긴 조각들이고
 * (끊긴 곳 없이 한 바퀴면 polygon, 원통 뒷면·이음매로 끊기면 polyline), 핸들은 휜 자리에 놓는다.
 */
export const WarpedSelectionOverlay = ({
  slot,
  node,
  photo,
  box,
  onPointerDownHandle,
  rotating = false,
}: WarpedSelectionOverlayProps): ReactElement => {
  const k = box.width / photo.widthPx;
  const toScreen = (point: { x: number; y: number }) => ({
    x: box.left + point.x * k,
    y: box.top + point.y * k,
  });
  const unflipped = node.type === 'image' ? { ...node, flipX: false, flipY: false } : node;
  const matrix = nodeMatrix(unflipped);
  const corners = [
    { x: 0, y: 0 },
    { x: node.width, y: 0 },
    { x: node.width, y: node.height },
    { x: 0, y: node.height },
  ].map((corner) => {
    const p = apply(matrix, corner);
    return { xMm: p.x, yMm: p.y };
  });
  const pieces = warpedRingPieces(slot, photo, corners, ringStepOf(node));
  return (
    <svg
      className="pointer-events-none absolute inset-0 size-full"
      aria-hidden
      data-testid="warped-selection-ring"
    >
      {pieces.map((piece, index) => {
        const points = piece.points
          .map((point) => {
            const p = toScreen(point);
            return `${p.x},${p.y}`;
          })
          .join(' ');
        const Shape = piece.closed ? 'polygon' : 'polyline';
        return (
          <Shape
            // biome-ignore lint/suspicious/noArrayIndexKey: 조각은 순서만 있고 다른 식별자가 없다.
            key={index}
            points={points}
            fill="none"
            stroke="currentColor"
            strokeWidth={1}
          />
        );
      })}
      {warpedHandles(slot, photo, node, k).map((handle) => {
        const p = toScreen(handle);
        if (handle.kind === 'rotate')
          return (
            <RotateHandle
              key={handle.kind}
              x={p.x}
              y={p.y}
              rotating={rotating}
              onPointerDown={(event) => onPointerDownHandle(handle.kind, event)}
            />
          );
        return (
          <circle
            key={handle.kind}
            cx={p.x}
            cy={p.y}
            r={5}
            className="pointer-events-auto fill-background stroke-current"
            style={{ cursor: handleCursor(handle.kind, node.rotationDeg) }}
            data-handle={handle.kind}
            onPointerDown={(event) => onPointerDownHandle(handle.kind, event)}
          />
        );
      })}
    </svg>
  );
};
